import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    ClipboardText, Stethoscope,
    Tray, ArrowsClockwise, PencilSimple,
    Factory, CheckCircle, CaretDown,
    ChatCircleDots, ShieldWarning,
} from "@phosphor-icons/react";
import { SLAPill } from "@/components/case/SLAHelpers";
import { FilterSearchBar } from "@/components/doctor/FilterBar";

/**
 * Planner queue — list-only.
 *
 * Each row links to /coe/planner/cases/:id (a dedicated full page).
 * Clicking a row passes ?from=<bucket> so the detail page's "Back to queue"
 * returns the planner to the same filter view they came from.
 */

const STATUS_PILLS = {
    submitted:            { label: "New",              cls: "bg-amber-100 text-amber-700" },
    assigned_to_planner:  { label: "Pending acceptance", cls: "bg-amber-100 text-amber-800" },
    refinement_requested: { label: "Refinement",       cls: "bg-amber-100 text-amber-800" },
    in_planning:          { label: "In planning",      cls: "bg-violet-100 text-violet-700" },
    submitted_to_qa:      { label: "Sent to QA",       cls: "bg-blue-100 text-blue-700" },
    sent_for_approval:    { label: "With doctor",      cls: "bg-cyan-100 text-cyan-700" },
    ready_for_review:     { label: "With doctor",      cls: "bg-cyan-100 text-cyan-700" },
    info_requested:       { label: "ADR — hold",       cls: "bg-orange-100 text-orange-700" },
    revision_requested:   { label: "Plan revision",    cls: "bg-rose-100 text-rose-700" },
    approved:             { label: "Approved",         cls: "bg-emerald-100 text-emerald-700" },
    // iter101.52 — Post-planner statuses. Previously fell through to
    // "New" pill which was misleading — case in awaiting_production_
    // assignment was showing as an actionable NEW.
    approved_pending_mfg:            { label: "MFG spec pending",   cls: "bg-rose-100 text-rose-800" },
    awaiting_production_assignment:  { label: "Awaiting production", cls: "bg-slate-100 text-slate-700" },
    submitted_to_production:         { label: "In production",      cls: "bg-slate-100 text-slate-700" },
    in_production:                   { label: "In production",      cls: "bg-slate-100 text-slate-700" },
    shipped:                         { label: "Shipped",            cls: "bg-slate-100 text-slate-700" },
    completed:                       { label: "Completed",          cls: "bg-emerald-50 text-emerald-700" },
};

const SCAN_KINDS = ["stl", "scan_upper", "scan_lower", "scan_bite"];
const PHOTO_KINDS = ["intraoral", "intraoral_front", "intraoral_left", "intraoral_right", "intraoral_upper_occlusal", "intraoral_lower_occlusal", "extraoral_front", "extraoral_profile"];
const XRAY_KINDS = ["xray_opg", "xray_lateral_ceph"];

/* iter101.51 — Planner-facing SLA-derived priority (Q1 = "both"). Runs
   alongside the doctor-picked Mfg speed to give the planner two
   signals at once: URGENT / DUE SOON / OK, computed from `hoursSince
   (status_updated_at)` against the SLA thresholds already used for
   the SLA pill. */
function plannerSLAPriority(hrs) {
    if (hrs > SLA_HOURS_BREACH) return { key: "urgent", label: "URGENT", cls: "bg-rose-100 text-rose-800 border-rose-200" };
    if (hrs > SLA_HOURS_WARN) return { key: "due", label: "DUE SOON", cls: "bg-amber-100 text-amber-800 border-amber-200" };
    return { key: "ok", label: "OK", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
}

/* iter101.51 — Compact "last activity" one-liner. Reads the last few
   timeline events and picks the most human-relevant. Falls back to
   the status pill's own label when the timeline is empty. */
function lastActivityLabel(c) {
    const tl = c.timeline || [];
    if (!tl.length) return null;
    const last = tl[tl.length - 1];
    const at = last.created_at ? new Date(last.created_at).getTime() : 0;
    const nowMs = Date.now();
    const mins = Math.max(0, Math.round((nowMs - at) / 60000));
    const rel = mins < 1 ? "just now"
        : mins < 60 ? `${mins}m ago`
        : mins < 60 * 24 ? `${Math.floor(mins / 60)}h ago`
        : `${Math.floor(mins / (60 * 24))}d ago`;
    const ACTION_COPY = {
        submitted: "Doctor submitted",
        planner_assigned: "Admin routed to you",
        planner_accepted: "You accepted",
        info_requested: "You raised ADR",
        info_provided: "Doctor replied",
        case_revision_requested: "Doctor asked revision",
        revision_requested: "Doctor asked revision",
        plan_submitted: "You submitted plan",
        approved: "Doctor approved",
        mfg_spec_submitted: "You submitted MFG",
        mfg_spec_resubmitted: "You resubmitted MFG",
        unlock_requested: "Unlock requested",
        unlock_resolved: "Unlock resolved",
    };
    const label = ACTION_COPY[last.action] || last.action?.replace(/_/g, " ") || "Last activity";
    return `${label} · ${rel}`;
}

/* Single source of truth for which bucket a case lives in.
   Mirrors the exact logic the sidebar sub-nav uses.
   iter101.5 — Added `mfg_spec_pending` for the post-approval window
   where the planner still owes the MFG spec before Admin can route
   to production.
   iter101.48 — Merged the former `awaiting_production` bucket into
   `planning_completed` (was `recently_completed`). Per user
   direction, once the planner has submitted the MFG spec the case
   has left their queue — a separate "Awaiting production" chip
   just added noise. Anything from `awaiting_production_assignment`
   through `completed` (within a 30-day rolling window) now surfaces
   under one calm "Planning completed" bucket. */
function bucketOf(c) {
    const tl = c.timeline || [];
    const lastDoctorRevision = [...tl].reverse().find((e) => e.action === "revision_requested" || e.action === "case_revision_requested");
    const s = c.status;
    if (s === "assigned_to_planner") return "pending_acceptance";
    if (s === "submitted") return "new"; // legacy / pre-routing state
    if (s === "info_requested") return "adr";
    if (s === "revision_requested") return "plan_revision";
    if (s === "refinement_requested") return "refinement";
    if (s === "sent_for_approval" || s === "ready_for_review") return "with_doctor";
    if (s === "approved_pending_mfg") return "mfg_spec_pending";
    if (["awaiting_production_assignment", "submitted_to_production", "in_production", "shipped", "completed"].includes(s)) {
        // Only surface in "Planning completed" if the case status
        // has moved within the last 30 days — older completions
        // are archived out of the planner's view to keep the list
        // bounded.
        const iso = c.status_updated_at || c.updated_at || c.completed_at;
        if (iso && (Date.now() - new Date(iso).getTime()) / 86400000 <= 30) {
            return "planning_completed";
        }
        return "archived";
    }
    if (s === "in_planning") {
        if (lastDoctorRevision) return "plan_revision";
        return "in_planning";
    }
    return "other";
}

const BUCKETS = [
    { id: "all",                 label: "My queue",            Icon: Tray,            tone: "primary",  hint: "Everything currently on your plate" },
    { id: "pending_acceptance",  label: "Pending acceptance",  Icon: ClipboardText,   tone: "amber",    hint: "Admin routed it — accept to start planning" },
    { id: "in_planning",         label: "In planning",         Icon: PencilSimple,    tone: "primary",  hint: "Actively planning" },
    { id: "plan_revision",       label: "Plan revision",       Icon: ArrowsClockwise, tone: "amber",    hint: "Doctor wants changes on plan" },
    { id: "adr",                 label: "ADR — hold",          Icon: ShieldWarning,   tone: "orange",   hint: "Awaiting doctor's additional documentation" },
    { id: "with_doctor",         label: "Submitted to doctor", Icon: Stethoscope,     tone: "cyan",     hint: "Awaiting doctor approval" },
    // iter101.5 — Doctor already approved. Planner still owes the MFG
    // spec before Admin can route to production. Rose tone flags it as
    // "this is on YOU right now" so it doesn't sit forgotten.
    { id: "mfg_spec_pending",    label: "MFG spec pending",    Icon: Factory,         tone: "rose",     hint: "Doctor approved · finalise manufacturing spec" },
    { id: "refinement",          label: "In refinement",       Icon: ArrowsClockwise, tone: "amber",    hint: "Mid-treatment refinement" },
    // iter101.48 — Renamed from "Recently completed" and folded in the
    // former "Awaiting production" chip. From the planner's POV, once
    // they submit the MFG spec the case IS complete for them — even
    // though its case status may show "In Production" etc downstream.
    { id: "planning_completed",  label: "Planning completed",  Icon: CheckCircle,     tone: "emerald",  hint: "You have finalised the MFG spec · calm handoff view" },
];

const SLA_HOURS_WARN = 24;
const SLA_HOURS_BREACH = 48;

function hoursSince(iso) {
    if (!iso) return 0;
    return Math.max(0, (Date.now() - new Date(iso).getTime()) / 3600000);
}

export default function PlannerQueue() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [cases, setCases] = useState([]);
    /* `q` is initialized from the URL so deep links from the case-detail
       "Open all cases for this patient" land here pre-filtered. */
    const initialQ = searchParams.get("q") || "";
    const [q, setQ] = useState(initialQ);
    const bucket = searchParams.get("bucket") || "all";
    /* `sort` URL param drives row ordering. Default "newest" mirrors the
       backend default (status_updated_at desc). Tile clicks from the
       dashboard land here with sort=oldest so the planner sees the most
       SLA-breached cases at the top of the filtered list. */
    const sortMode = searchParams.get("sort") === "oldest" ? "oldest" : "newest";
    const setSortMode = (m) => {
        const next = new URLSearchParams(searchParams);
        if (m === "newest") next.delete("sort"); else next.set("sort", m);
        setSearchParams(next, { replace: true });
    };
    const setBucket = (b) => {
        const next = new URLSearchParams(searchParams);
        if (b === "all") next.delete("bucket"); else next.set("bucket", b);
        setSearchParams(next, { replace: true });
    };
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        api.get("/planner/queue")
            .then((r) => setCases(r.data))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    /* Keep URL in sync with the search box so paste-and-share works. */
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (q) next.set("q", q); else next.delete("q");
        // Only call setSearchParams if there's an actual diff (avoids an
        // unnecessary navigation loop on initial mount).
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    const counts = useMemo(() => {
        const out = { all: 0 };
        BUCKETS.forEach((b) => { if (b.id !== "all") out[b.id] = 0; });
        cases.forEach((c) => {
            const b = bucketOf(c);
            if (out[b] !== undefined) out[b] += 1;
            // iter101.28 — "My queue" tally counts everything still in a
            // real bucket (excludes fully-archived work >30d old).
            if (b !== "archived" && b !== "other") out.all += 1;
        });
        return out;
    }, [cases]);

    /* Smart search: detect typed prefix and apply exact / prefix match
       against the right field. Falls back to fuzzy contains on name +
       doctor + diagnosis. Sort by oldest first when the URL says so —
       used by dashboard tile clicks to drop the planner straight onto
       the most SLA-breached row of the bucket. */
    /* iter101.51 — Global rank across all buckets (Q2=B). The rank is
       computed against the FULL cases list sorted by the current
       sortMode, so filtering by a bucket doesn't reset the numbers.
       "MFG spec pending · #14" means it's the planner's 14th case
       overall, giving them a stable sense of throughput. */
    const globalRankById = useMemo(() => {
        // Same sort logic as `filtered` but applied to the ENTIRE
        // cases list (no bucket filter, no search filter).
        const universe = cases.filter((c) => {
            const b = bucketOf(c);
            return b !== "archived" && b !== "other";
        });
        let ranked;
        if (sortMode === "oldest") {
            ranked = [...universe].sort((a, b) => {
                const ta = new Date(a.status_updated_at || a.updated_at || a.created_at || 0).getTime();
                const tb = new Date(b.status_updated_at || b.updated_at || b.created_at || 0).getTime();
                return ta - tb;
            });
        } else {
            ranked = [...universe].sort((a, b) => {
                const aResp = a.info_provided_at ? new Date(a.info_provided_at).getTime() : 0;
                const bResp = b.info_provided_at ? new Date(b.info_provided_at).getTime() : 0;
                if (aResp && !bResp) return -1;
                if (bResp && !aResp) return 1;
                if (aResp && bResp) return bResp - aResp;
                const ta = new Date(a.status_updated_at || a.updated_at || a.created_at || 0).getTime();
                const tb = new Date(b.status_updated_at || b.updated_at || b.created_at || 0).getTime();
                return tb - ta;
            });
        }
        const map = new Map();
        ranked.forEach((c, i) => map.set(c.id, i + 1));
        return map;
    }, [cases, sortMode]);

    const filtered = useMemo(() => {
        let list = cases;
        if (bucket !== "all") {
            list = list.filter((c) => bucketOf(c) === bucket);
        } else {
            // iter101.28 — Default "my queue" view: everything that's still
            // waiting for planner action OR was handed off in the last 30
            // days. Hides fully archived cases (older completions) which
            // would just add noise.
            list = list.filter((c) => {
                const b = bucketOf(c);
                return b !== "archived" && b !== "other";
            });
        }

        const raw = q.trim();
        if (raw) {
            const upper = raw.toUpperCase();
            const lower = raw.toLowerCase();
            const isPatientCode = upper.startsWith("INV-PT-") || upper.startsWith("IVQ-PID-");
            const isCaseCode = upper.startsWith("INV-CASE-") || upper.startsWith("IVQ-CRN-");
            if (isPatientCode) {
                // Match every case for this patient (all stages, ignore bucket
                // filter to surface history).
                list = cases.filter((c) => (c.patient_code || "").toUpperCase() === upper
                    || (c.patient_code || "").toUpperCase().startsWith(upper));
            } else if (isCaseCode) {
                list = list.filter((c) => (c.code || "").toUpperCase() === upper
                    || (c.code || "").toUpperCase().startsWith(upper));
            } else {
                list = list.filter((c) =>
                    (c.code || "").toLowerCase().includes(lower) ||
                    (c.patient_code || "").toLowerCase().includes(lower) ||
                    (c.patient_name || "").toLowerCase().includes(lower) ||
                    (c.doctor_name || "").toLowerCase().includes(lower) ||
                    (c.diagnosis || "").toLowerCase().includes(lower)
                );
            }
        }

        if (sortMode === "oldest") {
            // Stable sort by status_updated_at asc — oldest = most-at-risk
            // for SLA. Falls back to created_at when status hasn't moved.
            list = [...list].sort((a, b) => {
                const ta = new Date(a.status_updated_at || a.updated_at || a.created_at || 0).getTime();
                const tb = new Date(b.status_updated_at || b.updated_at || b.created_at || 0).getTime();
                return ta - tb;
            });
        } else {
            // Default "newest first" — but cases the doctor just resubmitted
            // (info_provided_at set, not yet acknowledged by the planner)
            // bubble to the very top regardless. This is the whole point of
            // the badge: in a 100-case queue, the planner immediately sees
            // which ADR-hold cases came back today.
            list = [...list].sort((a, b) => {
                const aResp = a.info_provided_at ? new Date(a.info_provided_at).getTime() : 0;
                const bResp = b.info_provided_at ? new Date(b.info_provided_at).getTime() : 0;
                if (aResp && !bResp) return -1;
                if (bResp && !aResp) return 1;
                if (aResp && bResp) return bResp - aResp;
                const ta = new Date(a.status_updated_at || a.updated_at || a.created_at || 0).getTime();
                const tb = new Date(b.status_updated_at || b.updated_at || b.created_at || 0).getTime();
                return tb - ta;
            });
        }
        return list;
    }, [cases, q, bucket, sortMode]);

    /* Detect the active search "mode" so we can show a small contextual
       hint under the input — "Showing all 3 cases for IVQ-PID-2526-0042" etc.
       Both pre-2026 (INV-PT-, INV-CASE-) and new-format (IVQ-PID-, IVQ-CRN-)
       codes are recognised so the search box keeps working for old records. */
    const searchMode = useMemo(() => {
        const upper = q.trim().toUpperCase();
        if (upper.startsWith("INV-PT-") || upper.startsWith("IVQ-PID-")) return "patient";
        if (upper.startsWith("INV-CASE-") || upper.startsWith("IVQ-CRN-")) return "case";
        if (q.trim()) return "fuzzy";
        return null;
    }, [q]);

    const openCase = (caseId) => {
        // Pass current bucket as `?from=` so detail page can return the planner here.
        const from = bucket === "all" ? "" : `?from=${bucket}`;
        navigate(`/coe/planner/cases/${caseId}${from}`);
    };

    return (
        <div data-testid="planner-queue" className="space-y-6">
            {/* HEADER — iter101.28 · Aligned to /admin/patients + /admin/cases
                grammar (text-2xl H1 + text-xs subtitle, no hero card wrapper).
                Removed: big rounded card, "COE · My Queue" overline eyebrow,
                inline productivity ribbon, and the 7 large BucketCards
                (redundant with the FilterChipRow below). */}
            <div>
                <h1 className="font-display text-2xl font-bold tracking-tight">My Queue</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    Every active case routed to you. Filter by stage, drill in to plan, review or hand off.
                </p>
            </div>

            {/* KPI STRIP — iter101.58 · Fixed 5-column grid on desktop
                per user direction ("can we show 5 in a row please").
                With 9 tiles this produces a clean 5+4 layout rather
                than the previous 4+4+1 auto-fit orphan. Falls back to
                3 cols on medium screens and 2 cols on mobile. */}
            <div
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
                data-testid="planner-queue-kpis"
            >
                <PlannerStatTile
                    testid="planner-kpi-all" label="My queue" value={counts.all || 0}
                    Icon={Tray} tone="primary" hint="Everything active"
                    onClick={() => setBucket("all")} active={bucket === "all"}
                />
                <PlannerStatTile
                    testid="planner-kpi-pending-acceptance" label="Pending acceptance" value={counts.pending_acceptance || 0}
                    Icon={ClipboardText} tone="amber" hint="Admin routed · needs accept"
                    onClick={() => setBucket("pending_acceptance")} active={bucket === "pending_acceptance"}
                />
                <PlannerStatTile
                    testid="planner-kpi-in-planning" label="In planning" value={counts.in_planning || 0}
                    Icon={PencilSimple} tone="violet" hint="Actively yours"
                    onClick={() => setBucket("in_planning")} active={bucket === "in_planning"}
                />
                <PlannerStatTile
                    testid="planner-kpi-plan-revision" label="Plan revision" value={counts.plan_revision || 0}
                    Icon={ArrowsClockwise} tone="amber" hint="Doctor wants changes"
                    onClick={() => setBucket("plan_revision")} active={bucket === "plan_revision"}
                />
                <PlannerStatTile
                    testid="planner-kpi-adr" label="ADR — hold" value={counts.adr || 0}
                    Icon={ShieldWarning} tone="orange" hint="Awaiting doctor info"
                    onClick={() => setBucket("adr")} active={bucket === "adr"}
                />
                <PlannerStatTile
                    testid="planner-kpi-with-doctor" label="With doctor" value={counts.with_doctor || 0}
                    Icon={Stethoscope} tone="cyan" hint="Awaiting approval"
                    onClick={() => setBucket("with_doctor")} active={bucket === "with_doctor"}
                />
                <PlannerStatTile
                    testid="planner-kpi-mfg-spec-pending" label="MFG spec pending" value={counts.mfg_spec_pending || 0}
                    Icon={Factory} tone="rose" hint="Approved · finalise spec"
                    onClick={() => setBucket("mfg_spec_pending")} active={bucket === "mfg_spec_pending"}
                />
                <PlannerStatTile
                    testid="planner-kpi-refinement" label="In refinement" value={counts.refinement || 0}
                    Icon={ArrowsClockwise} tone="amber" hint="Mid-treatment cycle"
                    onClick={() => setBucket("refinement")} active={bucket === "refinement"}
                />
                <PlannerStatTile
                    testid="planner-kpi-planning-completed" label="Planning completed" value={counts.planning_completed || 0}
                    Icon={CheckCircle} tone="emerald" hint="Calm handoff view"
                    onClick={() => setBucket("planning_completed")} active={bucket === "planning_completed"}
                />
            </div>

            {/* FILTER STRIP — iter101.28 · Same grammar as /admin/patients +
                /admin/cases. Search bar on the left, sort + view-toggle
                dropdowns in the right slot, FilterChipRow covers every
                bucket (including the two new handed-off ones). Sticky so
                filters stay reachable while the planner scrolls the list.
                iter101.52 — Removed the `bg-white/85 backdrop-blur-md`
                sticky band. It created a distinct grey box across the
                middle of the page. The filters still stick to the top,
                but without a visible band the page reads as one
                continuous surface. */}
            <div
                className="sticky top-16 z-20 py-3 flex flex-col gap-3"
                data-testid="planner-filter-bar"
            >
                <FilterSearchBar
                    query={q}
                    setQuery={setQ}
                    placeholder="Search case, patient, doctor — or paste IVQ-PID-… / IVQ-CRN-…"
                    searchTestid="planner-search"
                    testid="planner-filter-search"
                    rightSlot={(
                        <div className="relative">
                            <select
                                value={sortMode}
                                onChange={(e) => setSortMode(e.target.value)}
                                className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-semibold text-slate-600 hover:text-teal-800 hover:border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400 cursor-pointer transition-colors"
                                data-testid="planner-sort-select"
                                aria-label="Sort by"
                            >
                                <option value="newest">Newest first</option>
                                <option value="oldest">Oldest first (SLA)</option>
                            </select>
                            <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                        </div>
                    )}
                />
                {/* Stage Rail removed per user direction (iter101.55).
                    Bucket filtering happens via the KPI Deck tiles
                    above; search box handles case/patient lookup. */}

                {searchMode && (
                    <div className="text-[11px] text-muted-foreground -mt-1 pl-1" data-testid="planner-search-hint">
                        {searchMode === "patient" && (
                            <>
                                Showing <span className="font-semibold text-primary">{filtered.length}</span> case{filtered.length === 1 ? "" : "s"} for patient{" "}
                                <span className="font-mono text-primary">{q.trim().toUpperCase()}</span> across all stages.
                            </>
                        )}
                        {searchMode === "case" && (
                            <>
                                Looking up case <span className="font-mono text-primary">{q.trim().toUpperCase()}</span> in your queue.
                            </>
                        )}
                        {searchMode === "fuzzy" && (
                            <>
                                <span className="font-semibold">{filtered.length}</span> match{filtered.length === 1 ? "" : "es"} for &ldquo;{q.trim()}&rdquo;.
                                Tip: paste <span className="font-mono">IVQ-PID-…</span> to see every case for a patient.
                            </>
                        )}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="text-muted-foreground text-sm text-center py-10">Loading queue…</div>
            ) : filtered.length === 0 ? (
                <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground" data-testid="planner-empty">
                    <ClipboardText size={28} className="mx-auto opacity-50 mb-3" />
                    {q || bucket !== "all" ? "No cases match your filters." : "Nothing in your queue. New submissions from doctors will appear here."}
                </div>
            ) : (
                <div
                    className="rounded-2xl border border-border/70 bg-card overflow-hidden shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)]"
                    data-testid="planner-queue-list"
                >
                    {/* Table caption strip — subtle count summary above
                        the columns. Reads naturally as "Showing N cases"
                        and doesn't compete with the sticky filter bar. */}
                    <div className="flex items-center justify-between px-4 py-2 border-b border-border/70 bg-gradient-to-b from-teal-50/30 to-transparent">
                        <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-teal-800/80">
                            {bucket === "all" ? "Active queue" : (BUCKETS.find((b) => b.id === bucket)?.label || "Queue")}
                            <span className="text-teal-800/40 mx-1.5">·</span>
                            <span className="tabular-nums">{filtered.length}</span>
                            <span className="text-teal-800/60 font-semibold normal-case tracking-normal ml-1">case{filtered.length === 1 ? "" : "s"}</span>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-[10px] text-muted-foreground/80">
                            <span className="inline-flex items-center gap-1">
                                <span className="w-1 h-3 rounded-sm bg-rose-500" aria-hidden />
                                Urgent
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <span className="w-1 h-3 rounded-sm bg-amber-500" aria-hidden />
                                Due soon
                            </span>
                        </div>
                    </div>
                    {/* iter101.53 — Rebuilt as a real HTML table so the
                        column widths in header + rows are computed
                        together (native table layout), not separately
                        per-grid. This fixes the misalignment where
                        STATUS / PRIORITY / AGE labels sat above cells
                        of different widths. */}
                    <table className="w-full table-fixed text-[11.5px]" data-testid="planner-queue-table">
                        <colgroup>
                            <col className="w-10" />
                            <col />
                            <col />
                            <col className="w-[160px]" />
                            <col className="w-[130px]" />
                            <col className="w-[180px]" />
                            <col className="w-[90px]" />
                        </colgroup>
                        <thead className="bg-teal-50/40 border-b border-border/70">
                            <tr className="text-[9.5px] uppercase tracking-[0.16em] text-teal-800/80 font-bold">
                                <th className="py-2.5 text-center">#</th>
                                <th className="px-3 py-2.5 text-left font-bold">Case &amp; Patient</th>
                                <th className="px-3 py-2.5 text-left font-bold">Provider</th>
                                <th className="px-3 py-2.5 text-center font-bold whitespace-nowrap">Status</th>
                                <th className="px-3 py-2.5 text-center font-bold whitespace-nowrap">Priority</th>
                                <th className="px-3 py-2.5 text-left font-bold">Last activity</th>
                                <th className="px-3 py-2.5 text-right font-bold whitespace-nowrap">Age</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => (
                                <PlannerCaseRow
                                    key={c.id}
                                    c={c}
                                    rank={globalRankById.get(c.id) || 0}
                                    onOpen={() => openCase(c.id)}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* iter101.54 — PlannerStatTile redesigned to match the Command Bar +
   Workspace Deck visual language on PlannerCaseDetail. Each tile is
   a small "cockpit card": tone-coded 2px top accent bar, icon-disc
   with idle/active states mirroring the Workspace Deck, caps kicker
   label, editorial display number, and a small hint sub-line.
   Active state (bucket === this) lights the whole card with a soft
   teal-tinted background + teal ring — matches the active tab pill
   in the Workspace Deck for cross-page consistency. */
function PlannerStatTile({ testid, label, value, Icon, tone, hint, onClick, active }) {
    /* Tone tokens — top accent bar + idle icon disc + active pill. */
    const toneToken = {
        primary: { bar: "bg-teal-500",    idleDisc: "bg-teal-50 border-teal-200/70 text-teal-700",       activeDisc: "from-teal-500 to-teal-600" },
        amber:   { bar: "bg-amber-500",   idleDisc: "bg-amber-50 border-amber-200/70 text-amber-700",    activeDisc: "from-amber-500 to-amber-600" },
        violet:  { bar: "bg-violet-500",  idleDisc: "bg-violet-50 border-violet-200/70 text-violet-700", activeDisc: "from-violet-500 to-violet-600" },
        cyan:    { bar: "bg-cyan-500",    idleDisc: "bg-cyan-50 border-cyan-200/70 text-cyan-700",       activeDisc: "from-cyan-500 to-cyan-600" },
        rose:    { bar: "bg-rose-500",    idleDisc: "bg-rose-50 border-rose-200/70 text-rose-700",       activeDisc: "from-rose-500 to-rose-600" },
        emerald: { bar: "bg-emerald-500", idleDisc: "bg-emerald-50 border-emerald-200/70 text-emerald-700", activeDisc: "from-emerald-500 to-emerald-600" },
        orange:  { bar: "bg-orange-500",  idleDisc: "bg-orange-50 border-orange-200/70 text-orange-700", activeDisc: "from-orange-500 to-orange-600" },
    }[tone] || { bar: "bg-teal-500", idleDisc: "bg-teal-50 border-teal-200/70 text-teal-700", activeDisc: "from-teal-500 to-teal-600" };

    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={[
                "group relative overflow-hidden rounded-2xl border text-left p-3.5 pt-4",
                "transition-all duration-200 ease-out",
                active
                    ? "bg-gradient-to-b from-teal-50/70 to-white border-teal-300 ring-1 ring-teal-300/60 shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_10px_26px_-16px_rgba(20,184,166,0.35)]"
                    : "bg-card border-border/70 hover:border-teal-300/70 hover:shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_8px_20px_-14px_rgba(15,23,42,0.15)]",
            ].join(" ")}
        >
            {/* Tone-coded top accent bar — signals the semantic
                colour of the bucket without shouting. */}
            <span
                aria-hidden
                className={`absolute top-0 left-0 right-0 h-[2px] ${toneToken.bar} opacity-90`}
            />

            <div className="flex items-start gap-3">
                {/* Icon disc — teal-tinted when idle, tone-gradient with
                    white icon when the bucket is active. Mirrors the
                    Workspace Deck active-tab disc. */}
                <span
                    className={[
                        "w-9 h-9 rounded-full grid place-items-center shrink-0 border transition-all duration-200",
                        "group-hover:scale-[1.04]",
                        active
                            ? `bg-gradient-to-br ${toneToken.activeDisc} border-transparent text-white shadow-[inset_0_-1px_0_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.25)]`
                            : toneToken.idleDisc,
                    ].join(" ")}
                    aria-hidden
                >
                    <Icon size={16} weight="fill" />
                </span>

                <div className="min-w-0 flex-1">
                    <div className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground/80 truncate">
                        {label}
                    </div>
                    <div className={`font-display font-bold tabular-nums leading-none mt-1.5 text-[26px] ${active ? "text-teal-900" : "text-foreground"}`}>
                        {value}
                    </div>
                    {hint && (
                        <div className="text-[10px] text-muted-foreground/90 mt-1.5 truncate">{hint}</div>
                    )}
                </div>
            </div>
        </button>
    );
}

function PlannerCaseRow({ c, onOpen, rank = 0 }) {
    const status = STATUS_PILLS[c.status] || STATUS_PILLS.submitted;
    const doctorResponded = Boolean(c.info_provided_at);

    // Compact age display ("3h" / "2d") so the planner can scan how
    // long a case has been sitting in their queue at a glance.
    const hrs = Math.round(hoursSince(c.status_updated_at || c.updated_at || c.created_at));
    const ageLabel = hrs < 24 ? `${hrs}h` : `${Math.floor(hrs / 24)}d`;
    const ageTone = hrs > SLA_HOURS_BREACH ? "text-rose-700"
        : hrs > SLA_HOURS_WARN ? "text-amber-700"
        : "text-muted-foreground";

    /* iter101.51 — Priority cluster (Q1 = "both"):
         · Mfg-speed chip from `pricing.priority_label` (RUSH / STAT /
           STANDARD) — mirrors what the doctor picked at submission.
         · Planner-facing SLA-derived urgency (URGENT / DUE SOON / OK)
           computed from age against the same thresholds as the age
           column.
       Both chips live inside the Priority cell so the planner sees
       "why it's urgent to us + how fast the doctor wants it made". */
    const mfgSpeedRaw = (c.pricing?.priority_label || c.pricing?.priority_code || "").toString();
    const mfgSpeed = mfgSpeedRaw ? mfgSpeedRaw.toUpperCase() : null;
    const isPriorityMfg = mfgSpeed && !["", "STANDARD"].includes(mfgSpeed);
    const slaP = plannerSLAPriority(hrs);
    const activity = lastActivityLabel(c);

    /* iter101.54 — Left-edge priority stripe. Rendered as an inset
       box-shadow on the first cell (rank) so it lives *inside* the
       row without adding a wide extra column. Only surfaces for
       URGENT / DUE SOON so the OK cases stay visually calm — the
       eye immediately hunts for the coloured stripes when scanning
       a busy queue. */
    const stripeColor = slaP.key === "urgent"
        ? "rgb(244 63 94)"     // rose-500
        : slaP.key === "due"
        ? "rgb(245 158 11)"    // amber-500
        : "transparent";

    return (
        <tr
            onClick={onOpen}
            className={`cursor-pointer border-b border-border last:border-0 transition-colors ${
                doctorResponded
                    ? "bg-emerald-50/40 hover:bg-emerald-50/70"
                    : "hover:bg-teal-50/40"
            }`}
            data-testid={`planner-case-${c.id}`}
        >
            {/* # — Global rank. Also hosts the left-edge priority
                stripe via an inset box-shadow (see stripeColor above). */}
            <td
                className="py-3 text-center relative"
                data-testid={`planner-case-${c.id}-rank`}
                style={{ boxShadow: `inset 3px 0 0 0 ${stripeColor}` }}
            >
                <span
                    className="text-[11px] font-bold tabular-nums text-muted-foreground"
                    title={`#${rank} across your entire queue`}
                >
                    {rank || "—"}
                </span>
            </td>

            {/* Case & Patient — icon removed per user direction
                iter101.54; patient name is the primary anchor (top
                line), case code + badges sit below as the secondary
                (iter101.55 · re-ordered per user feedback that the
                previous layout looked like the case ID was duplicated
                — the two IVQ-... codes are actually distinct: the
                first is the case code, the second is the patient
                code, but visually they clashed). */}
            <td className="px-3 py-3 min-w-0">
                <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold text-foreground truncate">
                        {c.patient_name || "Patient"}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        {c.code && (
                            <span
                                className="font-mono text-[10.5px] font-semibold text-teal-900 tabular-nums whitespace-nowrap"
                                data-testid={`planner-case-${c.id}-code`}
                            >
                                {c.code}
                            </span>
                        )}
                        {(c.revision_count || 0) > 0 && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase font-semibold tracking-wide">
                                Rev {c.revision_count}
                            </span>
                        )}
                        {doctorResponded && (
                            <span
                                className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase font-bold tracking-wide inline-flex items-center gap-0.5 whitespace-nowrap"
                                data-testid={`planner-case-${c.id}-responded-badge`}
                                title="The doctor has supplied the info you asked for — open the case to review."
                            >
                                <ChatCircleDots size={9} weight="fill" /> Responded
                            </span>
                        )}
                    </div>
                </div>
            </td>

            {/* Prescribing doctor — doctor name + clinic name.
                Diagnosis moved out per user direction iter101.55; the
                planner will read the diagnosis on the case detail. */}
            <td className="px-3 py-3 min-w-0">
                <div className="text-[11.5px] font-semibold text-foreground/90 truncate">
                    Dr. {(c.doctor_name || "").replace(/^Dr\.\s*/, "") || "—"}
                </div>
                <div className="text-[10.5px] text-muted-foreground truncate mt-0.5">
                    {c.practice_location_name || c.clinic_name || "—"}
                </div>
            </td>

            {/* Status */}
            <td className="px-3 py-3 text-center whitespace-nowrap">
                <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${status.cls}`}>
                    {status.label}
                </span>
            </td>

            {/* Priority — Mfg + SLA */}
            <td className="px-3 py-3 text-center whitespace-nowrap" data-testid={`planner-case-${c.id}-priority`}>
                <div className="inline-flex items-center gap-1">
                    {isPriorityMfg && (
                        <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-fuchsia-100 border border-fuchsia-200 text-[9px] font-bold uppercase tracking-widest text-fuchsia-800"
                            title={`Doctor requested ${mfgSpeed} manufacturing speed`}
                        >
                            {mfgSpeed}
                        </span>
                    )}
                    <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-widest ${slaP.cls}`}
                        data-testid={`planner-case-${c.id}-priority-sla-${slaP.key}`}
                        title={`SLA-derived planner priority · ${hrs}h in current status`}
                    >
                        {slaP.label}
                    </span>
                </div>
            </td>

            {/* Last activity — small teal timeline dot + human label. */}
            <td className="px-3 py-3 min-w-0">
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground truncate max-w-[200px]" title={activity || ""}>
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-400/70 shrink-0" aria-hidden />
                    <span className="truncate">{activity || "—"}</span>
                </div>
            </td>

            {/* Age */}
            <td className="px-3 py-3 text-right whitespace-nowrap">
                <div className="inline-flex items-center gap-1.5">
                    <SLAPill c={c} compact />
                    <span className={`text-[11px] font-semibold tabular-nums ${ageTone}`}>{ageLabel}</span>
                </div>
            </td>
        </tr>
    );
}
